import RequestAbstract from "../abstract";
import { postEventStream } from "../sse";

/**
 * Scraper scenarios: named scripts x-scraper runs in a headless browser, which
 * xscraper gateway settings reference from their settings by name or by id.
 *
 * The CRUD half is the usual controller. Saving a scenario a settings row uses
 * changes what every gateway linking that row runs from the next call on — there
 * is no deploy step — and the database refuses a rename, archive or delete of one
 * that is in use with a 422 whose message carries TRIGGER_SCRAPER_SCENARIO_IN_USE.
 */
export default class PublicScraperScenarios extends RequestAbstract {
    data;
    constructor(data) {
        super(data);
        this.data = data;
        this.link = "api/public-scraper-scenarios";
    }

    /**
     * One scenario, full DTO. The controller has no GET by id, so this is the list
     * endpoint narrowed to the id — which, unlike the grid, applies no archive
     * filter, so an archived scenario still opens. -> envelope, `data` a list.
     */
    async getById(id) {
        return await this.search(1, [], [], [{ field: 'id', op: '=', value: Number(id) }]);
    }

    /** The settings actions that reference it. -> [{settingsId, settingsName, kind, action, linkedBy: 'id'|'name'}] */
    async usage(id) {
        return await this.get(`${this.link}/${id}/usage`);
    }

    /**
     * Runs code in x-scraper without saving it, streaming the run back.
     *
     * `body` is `{ scenarioId?, code, inputTemplate?, input?, gatewaySettingsId?, action? }`
     * — the editor's unsaved code and template. With gatewaySettingsId (an xscraper
     * settings row) and action the server layers the input as template < that
     * action's stored input < `input` and uses the row's scraper endpoint and proxy;
     * the stored input holds credentials, so it is never sent here.
     *
     * `onEvent(type, data)` gets every event — job, status, frame, url, log,
     * console, pageerror, then one of done / error / cancelled. Aborting `signal`
     * is the only way to stop a run: the server sees the connection close, cancels
     * the job and closes the browser. Resolves to an envelope (see
     * postEventStream); a refusal before the stream starts is the usual 422/400.
     *
     * With `mode: 'debug' | 'record'` (and `breakpoints`, lines of `code`) the run
     * also sends step, paused, resumed, recorded and select, takes previewControl
     * and previewInput, and its stream ends with `rotate` rather than at the time
     * limit: carry on with reattach().
     */
    async preview(body, { signal, onEvent } = {}) {
        return await this.stream(`${this.link}/preview`, body, { signal, onEvent });
    }

    /**
     * The same Debug or Record run's stream again, after `rotate`: the current
     * pause, and the logs and recorded lines after `since` (the last event's ts).
     */
    async reattach(jobId, since, { signal, onEvent } = {}) {
        const query = `attach=${encodeURIComponent(jobId)}${since ? `&since=${since}` : ''}`;
        return await this.stream(`${this.link}/preview?${query}`, {}, { signal, onEvent });
    }

    /** `{ command: 'continue' | 'step' | 'pause' }`, `{ command: 'breakpoints', lines }`, `{ command: 'record', on }`. */
    async previewControl(jobId, command) {
        return await this.post(`${this.link}/preview/${encodeURIComponent(jobId)}/control`, command);
    }

    /** A click, wheel, text, key, goto or select on a paused run's live view (see LiveBrowserView). */
    async previewInput(jobId, input) {
        return await this.post(`${this.link}/preview/${encodeURIComponent(jobId)}/input`, input);
    }

    async stream(link, body, { signal, onEvent }) {
        const { language } = this._config;
        const resp = await postEventStream(this.getFullLink(link), {
            // The same headers loadWithAuth sets; Content-Type comes from postEventStream.
            headers: {
                'X-Auth-Token': localStorage.token,
                'Language': localStorage.language || language
            },
            body,
            signal,
            onEvent
        });
        this.handleStatus(resp);
        return resp;
    }
}
